import React, { useState, useEffect } from 'react';
import { useAuth } from '../context/AuthContext';
import { useThemeColors, getPrimaryButtonStyles, getAccentButtonStyles } from '../hooks/useThemeColors';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../components/ui/dialog';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../components/ui/table';
import { Badge } from '../components/ui/badge';
import { Alert, AlertDescription } from '../components/ui/alert';
import { 
  Plus, Search, Edit, Trash2, Loader2, Building2, 
  AlertCircle, UserPlus, Eye, EyeOff
} from 'lucide-react';

const EmpresasPage = () => {
  const { api } = useAuth();
  const themeColors = useThemeColors();
  const [loading, setLoading] = useState(true);
  const [empresas, setEmpresas] = useState([]);
  const [search, setSearch] = useState('');
  
  const [dialogOpen, setDialogOpen] = useState(false);
  const [adminDialogOpen, setAdminDialogOpen] = useState(false);
  const [editingEmpresa, setEditingEmpresa] = useState(null);
  const [selectedEmpresa, setSelectedEmpresa] = useState(null);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [showPassword, setShowPassword] = useState(false);

  const [formData, setFormData] = useState({
    nombre: '',
    direccion: '',
    correo_principal: '',
    codigo: '',
  });

  const [adminData, setAdminData] = useState({
    nombre: '',
    correo: '',
    password: '',
  });

  const fetchEmpresas = async () => {
    setLoading(true);
    try {
      const response = await api.get('/empresas');
      setEmpresas(response.data);
    } catch (err) {
      console.error('Error fetching empresas:', err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchEmpresas();
  }, []);

  const openCreate = () => {
    setEditingEmpresa(null);
    setFormData({
      nombre: '',
      direccion: '',
      correo_principal: '',
      codigo: '',
    });
    setError('');
    setDialogOpen(true);
  };

  const openEdit = (empresa) => {
    setEditingEmpresa(empresa);
    setFormData({
      nombre: empresa.nombre,
      direccion: empresa.direccion || '',
      correo_principal: empresa.correo_principal || '',
      codigo: empresa.codigo,
    });
    setError('');
    setDialogOpen(true);
  };

  const openAdminDialog = (empresa) => {
    setSelectedEmpresa(empresa);
    setAdminData({
      nombre: '',
      correo: '',
      password: '',
    });
    setError('');
    setShowPassword(false);
    setAdminDialogOpen(true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setSaving(true);

    try {
      if (editingEmpresa) {
        const { codigo, ...updateData } = formData;
        await api.put(`/empresas/${editingEmpresa.id}`, updateData);
      } else {
        await api.post('/empresas', formData);
      }
      setDialogOpen(false);
      fetchEmpresas();
    } catch (err) {
      setError(err.response?.data?.detail || 'Error al guardar la empresa');
    } finally {
      setSaving(false);
    }
  };

  const handleCreateAdmin = async (e) => {
    e.preventDefault();
    setError('');
    setSaving(true);

    try {
      await api.post(`/empresas/${selectedEmpresa.id}/admin-inicial`, adminData);
      setAdminDialogOpen(false);
      fetchEmpresas();
    } catch (err) {
      setError(err.response?.data?.detail || 'Error al crear el administrador');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (empresa) => {
    if (!window.confirm(`¿Está seguro de desactivar la empresa "${empresa.nombre}"?`)) return;

    try {
      await api.delete(`/empresas/${empresa.id}`);
      fetchEmpresas();
    } catch (err) {
      console.error('Error deleting empresa:', err);
    }
  };

  const filteredEmpresas = empresas.filter(e => 
    e.nombre.toLowerCase().includes(search.toLowerCase()) ||
    e.codigo.toLowerCase().includes(search.toLowerCase())
  );

  return (
    <div className="space-y-6 animate-fadeIn" data-testid="empresas-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="font-heading text-2xl font-bold text-slate-900">Empresas</h1>
          <p className="text-slate-500 text-sm mt-1">
            Gestión de empresas del sistema (Solo SUPERADMIN)
          </p>
        </div>
        <Button 
          onClick={openCreate} 
          data-testid="crear-empresa-btn" 
          className="text-white"
          {...getPrimaryButtonStyles(themeColors)}
        >
          <Plus className="w-4 h-4 mr-2" />
          Nueva Empresa
        </Button>
      </div>

      {/* Search */}
      <Card className="border-slate-200">
        <CardContent className="p-4">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
            <Input
              placeholder="Buscar por nombre o código..."
              data-testid="busqueda-input"
              className="pl-10"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
            />
          </div>
        </CardContent>
      </Card>

      {/* Table */}
      <Card className="border-slate-200">
        <CardContent className="p-0">
          {loading ? (
            <div className="flex items-center justify-center py-12">
              <Loader2 className="w-8 h-8 animate-spin text-slate-400" />
            </div>
          ) : filteredEmpresas.length === 0 ? (
            <div className="flex flex-col items-center justify-center py-12 text-slate-500">
              <Building2 className="w-12 h-12 mb-4 text-slate-300" />
              <p>No se encontraron empresas</p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow className="table-header">
                    <TableHead>Código</TableHead>
                    <TableHead>Nombre</TableHead>
                    <TableHead className="hidden md:table-cell">Correo</TableHead>
                    <TableHead>Estado</TableHead>
                    <TableHead></TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {filteredEmpresas.map((empresa) => (
                    <TableRow key={empresa.id} className="table-row" data-testid={`empresa-row-${empresa.id}`}>
                      <TableCell className="font-mono font-medium text-sm">
                        {empresa.codigo}
                      </TableCell>
                      <TableCell className="font-medium">{empresa.nombre}</TableCell>
                      <TableCell className="hidden md:table-cell">
                        {empresa.correo_principal || '-'}
                      </TableCell>
                      <TableCell>
                        <Badge className={empresa.activo ? 'status-terminado' : 'bg-red-100 text-red-800'}>
                          {empresa.activo ? 'Activa' : 'Inactiva'}
                        </Badge>
                      </TableCell>
                      <TableCell>
                        <div className="flex gap-1">
                          <Button 
                            variant="ghost" 
                            size="icon"
                            onClick={() => openAdminDialog(empresa)}
                            title="Crear Administrador"
                            data-testid={`crear-admin-${empresa.id}`}
                          >
                            <UserPlus className="w-4 h-4" />
                          </Button>
                          <Button 
                            variant="ghost" 
                            size="icon"
                            onClick={() => openEdit(empresa)}
                            data-testid={`editar-empresa-${empresa.id}`}
                          >
                            <Edit className="w-4 h-4" />
                          </Button>
                          <Button 
                            variant="ghost" 
                            size="icon"
                            onClick={() => handleDelete(empresa)}
                            className="text-red-500 hover:text-red-700"
                          >
                            <Trash2 className="w-4 h-4" />
                          </Button>
                        </div>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Create/Edit Empresa Dialog */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Building2 className="w-5 h-5" />
              {editingEmpresa ? 'Editar Empresa' : 'Nueva Empresa'}
            </DialogTitle>
          </DialogHeader>

          <form onSubmit={handleSubmit} className="space-y-4">
            {error && (
              <Alert variant="destructive">
                <AlertCircle className="h-4 w-4" />
                <AlertDescription>{error}</AlertDescription>
              </Alert>
            )}

            <div className="space-y-2">
              <Label htmlFor="codigo">Código *</Label>
              <Input
                id="codigo"
                data-testid="codigo-input"
                value={formData.codigo}
                onChange={(e) => setFormData({ ...formData, codigo: e.target.value.toLowerCase().replace(/[^a-z0-9-]/g, '') })}
                placeholder="mi-empresa"
                disabled={!!editingEmpresa}
                required
              />
              <p className="text-xs text-slate-500">
                Se usará en la URL: /reclamos/{formData.codigo || 'codigo'}
              </p>
            </div>

            <div className="space-y-2">
              <Label htmlFor="nombre">Nombre de la Empresa *</Label>
              <Input
                id="nombre"
                data-testid="nombre-input"
                value={formData.nombre}
                onChange={(e) => setFormData({ ...formData, nombre: e.target.value })}
                placeholder="Grupo Inmobiliario XYZ"
                required
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="direccion">Dirección</Label>
              <Input
                id="direccion"
                data-testid="direccion-input"
                value={formData.direccion}
                onChange={(e) => setFormData({ ...formData, direccion: e.target.value })}
                placeholder="Av. Ejemplo 123"
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="correo_principal">Correo Principal</Label>
              <Input
                id="correo_principal"
                type="email"
                data-testid="correo-principal-input"
                value={formData.correo_principal}
                onChange={(e) => setFormData({ ...formData, correo_principal: e.target.value })}
                placeholder="contacto@empresa.com"
              />
            </div>

            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setDialogOpen(false)}>
                Cancelar
              </Button>
              <Button 
                type="submit" 
                disabled={saving}
                data-testid="guardar-empresa-btn"
                className="text-white"
                {...getPrimaryButtonStyles(themeColors)}
              >
                {saving ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : null}
                {editingEmpresa ? 'Actualizar' : 'Crear'}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Create Admin Dialog */}
      <Dialog open={adminDialogOpen} onOpenChange={setAdminDialogOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <UserPlus className="w-5 h-5" />
              Crear Administrador Inicial
            </DialogTitle>
          </DialogHeader>

          {selectedEmpresa && (
            <form onSubmit={handleCreateAdmin} className="space-y-4">
              <div className="p-3 bg-slate-50 rounded-lg">
                <p className="text-sm text-slate-600">Empresa:</p>
                <p className="font-medium">{selectedEmpresa.nombre}</p>
              </div>

              {error && (
                <Alert variant="destructive">
                  <AlertCircle className="h-4 w-4" />
                  <AlertDescription>{error}</AlertDescription>
                </Alert>
              )}

              <div className="space-y-2">
                <Label htmlFor="admin_nombre">Nombre del Administrador *</Label>
                <Input
                  id="admin_nombre"
                  data-testid="admin-nombre-input"
                  value={adminData.nombre}
                  onChange={(e) => setAdminData({ ...adminData, nombre: e.target.value })}
                  placeholder="Juan Pérez"
                  required
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="admin_correo">Correo *</Label>
                <Input
                  id="admin_correo"
                  type="email"
                  data-testid="admin-correo-input"
                  value={adminData.correo}
                  onChange={(e) => setAdminData({ ...adminData, correo: e.target.value })}
                  placeholder="admin@empresa.com"
                  required
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="admin_password">Contraseña *</Label>
                <div className="relative">
                  <Input
                    id="admin_password"
                    type={showPassword ? 'text' : 'password'}
                    data-testid="admin-password-input"
                    value={adminData.password}
                    onChange={(e) => setAdminData({ ...adminData, password: e.target.value })}
                    placeholder="••••••••"
                    required
                  />
                  <Button
                    type="button"
                    variant="ghost"
                    size="icon"
                    className="absolute right-0 top-0"
                    onClick={() => setShowPassword(!showPassword)}
                  >
                    {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                  </Button>
                </div>
              </div>

              <DialogFooter>
                <Button type="button" variant="outline" onClick={() => setAdminDialogOpen(false)}>
                  Cancelar
                </Button>
                <Button 
                  type="submit" 
                  disabled={saving}
                  data-testid="crear-admin-btn"
                  className="text-white"
                  {...getAccentButtonStyles(themeColors)}
                >
                  {saving ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : null}
                  Crear Administrador
                </Button>
              </DialogFooter>
            </form>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default EmpresasPage;
